import { Lexer } from 'marked';

/** Where an autolink sits in a text: `[start, end)` character offsets. */
export type TAutolinkRange = [start: number, end: number];

/** The parts of a marked token this walk reads. Table cells carry `text`, not `raw`. */
interface ISourceToken {
  type?: string;
  raw?: string;
  text?: string;
  tokens?: ISourceToken[];
  items?: ISourceToken[];
  /** A table's header cells; on a cell itself, whether it is one. */
  header?: ISourceToken[] | boolean;
  rows?: ISourceToken[][];
}

const sourceOf = (token: ISourceToken): string => token.raw ?? token.text ?? '';

/** A CommonMark autolink (`<scheme:…>` or `<local@domain>`), as opposed to a bare GFM URL. */
const isAutolink = (token: ISourceToken): boolean =>
  token.type === 'link' && sourceOf(token).startsWith('<');

const childrenOf = (token: ISourceToken): ISourceToken[] => {
  if (token.type === 'list') return token.items ?? [];
  if (token.type === 'table' && Array.isArray(token.header)) {
    return [...token.header, ...(token.rows ?? []).flat()];
  }
  return token.tokens ?? [];
};

/**
 * Records where each autolink inside a token sits. Children are found in
 * their parent's source in order. Code blocks and code spans have no
 * children, so an autolink inside one, which already renders literally, is
 * never recorded.
 */
const collect = (
  token: ISourceToken,
  offset: number,
  ranges: TAutolinkRange[],
): void => {
  const source = sourceOf(token);
  if (isAutolink(token)) {
    ranges.push([offset, offset + source.length]);
    return;
  }
  let cursor = 0;
  for (const child of childrenOf(token)) {
    const childSource = sourceOf(child);
    const start = source.indexOf(childSource, cursor);
    // Source the lexer normalised can't be mapped back; leave it as written.
    if (start === -1) return;
    collect(child, offset + start, ranges);
    cursor = start + childSource.length;
  }
};

/**
 * Finds a text's autolinks with the markdown lexer, block structure included.
 * Lex once and pass the result to the other helpers here.
 *
 * @param text - The text to render as markdown
 * @returns Where each autolink sits, in order
 */
export function findAutolinks(text: string): TAutolinkRange[] {
  const ranges: TAutolinkRange[] = [];
  collect({ raw: text, tokens: Lexer.lex(text) }, 0, ranges);
  return ranges;
}

/**
 * Wraps each autolink in a code span so it renders exactly as written.
 * Escaping the `<` alone isn't enough: GFM then re-links the bare address or
 * URL inside, and a URL swallows the closing `>` into its href.
 *
 * @param text - The text, or the start of it
 * @param autolinks - From {@link findAutolinks}; ranges past the end of `text` are skipped
 * @returns The text with its autolinks shown literally
 */
export function showAutolinksLiterally(
  text: string,
  autolinks: TAutolinkRange[],
): string {
  let shown = '';
  let cursor = 0;
  for (const [start, end] of autolinks) {
    if (end > text.length) break;
    const autolink = text.slice(start, end);
    const longestTick = Math.max(
      0,
      ...(autolink.match(/`+/g) ?? []).map(run => run.length),
    );
    const fence = '`'.repeat(longestTick + 1);
    shown += `${text.slice(cursor, start)}${fence}${autolink}${fence}`;
    cursor = end;
  }
  return shown + text.slice(cursor);
}

/**
 * Whether a text is nothing but one autolink, e.g. a Message-ID.
 *
 * @param text - The text
 * @param autolinks - From {@link findAutolinks}
 */
export function isSoleAutolink(
  text: string,
  autolinks: TAutolinkRange[],
): boolean {
  const [only] = autolinks;
  return autolinks.length === 1 && text.trim() === text.slice(only[0], only[1]);
}

/**
 * Where to cut a text to `maxLength` without splitting an autolink: an
 * autolink the cut would land inside is dropped whole, so what is left still
 * reads as markdown.
 *
 * @param maxLength - The longest the kept text may be
 * @param autolinks - From {@link findAutolinks}
 * @returns How many characters to keep
 */
export function cutBeforeAutolink(
  maxLength: number,
  autolinks: TAutolinkRange[],
): number {
  const split = autolinks.find(
    ([start, end]) => start < maxLength && maxLength < end,
  );
  return split ? split[0] : maxLength;
}
